import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  VStack,
  HStack,
  Button,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useToast,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useBreakpointValue,
  Link,
} from '@chakra-ui/react';
import DataTable from 'react-data-table-component';
import { FiEdit, FiTrash2, FiPlus, FiRefreshCw, FiExternalLink } from 'react-icons/fi';
import { ieemFlyerApi } from '../config/api';
import StatusChip from './bundling/StatusChip';
import ReadinessPanel from './bundling/ReadinessPanel';
import DataTableShell from './bundling/DataTableShell';
import SubheaderToolbar from './bundling/SubheaderToolbar';

const getFlyerStatus = (row) => {
  const hasAsset = !!(row.IEEMFLYER_URL || row.IEEMFLYER_FILE);
  return hasAsset ? 'Published' : 'Draft';
};

const getFlyerSummary = (rows) => {
  const total = rows.length;
  const published = rows.filter((row) => getFlyerStatus(row) === 'Published').length;
  const draft = total - published;
  return { total, published, draft };
};

const getFlyerBlockers = (rows) => {
  const blockers = [];
  const summary = getFlyerSummary(rows);
  if (summary.total === 0) blockers.push('Belum ada flyer untuk member ini.');
  if (summary.published === 0 && summary.total > 0) blockers.push('Belum ada flyer dengan file/URL valid.');
  return blockers;
};

const IeemFlyerTab = ({ memberCode }) => {
  const [ieemFlyerData, setIeemFlyerData] = useState([]);
  const [loadingIeemFlyer, setLoadingIeemFlyer] = useState(false);
  const [editingIeemFlyer, setEditingIeemFlyer] = useState(null);
  const [deleteIeemFlyer, setDeleteIeemFlyer] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const toast = useToast();
  const isMobileView = useBreakpointValue({ base: true, md: false });
  
  const { isOpen: isIeemFlyerFormOpen, onOpen: onIeemFlyerFormOpen, onClose: onIeemFlyerFormClose } = useDisclosure();
  const { isOpen: isIeemFlyerDeleteOpen, onOpen: onIeemFlyerDeleteOpen, onClose: onIeemFlyerDeleteClose } = useDisclosure();
  const cancelRef = React.useRef();

  // IEEM Flyer form data
  const [ieemFlyerFormData, setIeemFlyerFormData] = useState({
    IEEMFLYER_NAME: '',
    IEEMFLYER_DESC: '',
    IEEMFLYER_MEMBER: memberCode || '',
    IEEMFLYER_URL: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = React.useRef(null);

  // Fetch IEEM Flyer data
  const fetchIeemFlyer = useCallback(async () => {
    setLoadingIeemFlyer(true);
    try {
      const response = await ieemFlyerApi.get(`/list?member=${memberCode}`);
      if (response.data.status === 200) {
        setIeemFlyerData(response.data.data || []);
      } else {
        toast({
          title: 'Error',
          description: response.data.message || 'Gagal mengambil data IEEM Flyer',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching IEEM Flyer:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat mengambil data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingIeemFlyer(false);
    }
  }, [toast, memberCode]);

  useEffect(() => {
    fetchIeemFlyer();
  }, [fetchIeemFlyer]);

  // IEEM Flyer handlers
  const handleIeemFlyerInputChange = (e) => {
    const { name, value } = e.target;
    setIeemFlyerFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'File tidak valid',
          description: 'File harus berupa PDF atau gambar (JPG, PNG, GIF)',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        e.target.value = '';
        return;
      }
      
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'File terlalu besar',
          description: 'Ukuran file maksimal 10MB',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        e.target.value = '';
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleIeemFlyerCreate = () => {
    setEditingIeemFlyer(null);
    setIeemFlyerFormData({
      IEEMFLYER_NAME: '',
      IEEMFLYER_DESC: '',
      IEEMFLYER_MEMBER: memberCode || '',
      IEEMFLYER_URL: '',
    });
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onIeemFlyerFormOpen();
  };

  const handleIeemFlyerEdit = (item) => {
    setEditingIeemFlyer(item);
    setIeemFlyerFormData({
      IEEMFLYER_NAME: item.IEEMFLYER_NAME || '',
      IEEMFLYER_DESC: item.IEEMFLYER_DESC || '',
      IEEMFLYER_MEMBER: item.IEEMFLYER_MEMBER || memberCode || '',
      IEEMFLYER_URL: item.IEEMFLYER_URL || '',
    });
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onIeemFlyerFormOpen();
  };

  const handleIeemFlyerSubmit = async () => {
    if (!ieemFlyerFormData.IEEMFLYER_NAME || !ieemFlyerFormData.IEEMFLYER_MEMBER) {
      toast({
        title: 'Validasi gagal',
        description: 'Nama dan Member harus diisi',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // For create, file is required if URL is not provided
    if (!editingIeemFlyer && !selectedFile && !ieemFlyerFormData.IEEMFLYER_URL) {
      toast({
        title: 'Validasi gagal',
        description: 'File atau URL harus diisi',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setUploadingFile(true);

    try {
      const formData = new FormData();
      formData.append('IEEMFLYER_NAME', ieemFlyerFormData.IEEMFLYER_NAME);
      formData.append('IEEMFLYER_DESC', ieemFlyerFormData.IEEMFLYER_DESC || '');
      formData.append('IEEMFLYER_MEMBER', ieemFlyerFormData.IEEMFLYER_MEMBER);
      formData.append('IEEMFLYER_URL', ieemFlyerFormData.IEEMFLYER_URL || '');
      
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      if (editingIeemFlyer) {
        // Update
        const response = await ieemFlyerApi.post(`/update/${editingIeemFlyer.IEEMFLYER_ID}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        if (response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'IEEM Flyer berhasil diupdate',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onIeemFlyerFormClose();
          fetchIeemFlyer();
        }
      } else {
        // Create
        const response = await ieemFlyerApi.post('/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        if (response.data.status === 201 || response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'IEEM Flyer berhasil dibuat',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onIeemFlyerFormClose();
          fetchIeemFlyer();
        }
      }
    } catch (error) {
      console.error('Error saving IEEM Flyer:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menyimpan data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUploadingFile(false);
    }
  };

  const handleIeemFlyerDeleteClick = (item) => {
    setDeleteIeemFlyer(item);
    onIeemFlyerDeleteOpen();
  };

  const handleIeemFlyerDeleteConfirm = async () => {
    if (!deleteIeemFlyer) return;

    try {
      const response = await ieemFlyerApi.post(`/delete/${deleteIeemFlyer.IEEMFLYER_ID}`);
      if (response.data.status === 200) {
        toast({
          title: 'Berhasil',
          description: 'IEEM Flyer berhasil dihapus',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onIeemFlyerDeleteClose();
        setDeleteIeemFlyer(null);
        fetchIeemFlyer();
      }
    } catch (error) {
      console.error('Error deleting IEEM Flyer:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menghapus data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getFileUrl = (flyer) => {
    if (flyer.IEEMFLYER_URL) {
      return flyer.IEEMFLYER_URL;
    }
    if (flyer.IEEMFLYER_FILE) {
      return `/flyers/${flyer.IEEMFLYER_FILE}`;
    }
    return null;
  };

  // Custom styles for DataTable
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: '#f7fafc',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
      },
    },
    headCells: {
      style: {
        fontSize: '14px',
        fontWeight: '600',
        textTransform: 'none',
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    cells: {
      style: {
        fontSize: '14px',
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    rows: {
      style: {
        minHeight: '60px',
        '&:not(:last-of-type)': {
          borderBottom: '1px solid #e2e8f0',
        },
      },
    },
  };

  // Pagination component options
  const paginationComponentOptions = {
    rowsPerPageText: 'Baris per halaman:',
    rangeSeparatorText: 'dari',
    selectAllRowsItem: true,
    selectAllRowsItemText: 'Semua',
  };

  // Filter flyer data based on search term
  const filteredFlyerData = useMemo(() => {
    if (!searchTerm) return ieemFlyerData;
    const searchLower = searchTerm.toLowerCase();
    return ieemFlyerData.filter((flyer) => {
      return (
        (flyer.IEEMFLYER_NAME && flyer.IEEMFLYER_NAME.toLowerCase().includes(searchLower)) ||
        (flyer.IEEMFLYER_DESC && flyer.IEEMFLYER_DESC.toLowerCase().includes(searchLower)) ||
        (flyer.IEEMFLYER_ID && flyer.IEEMFLYER_ID.toString().includes(searchLower)) ||
        (flyer.IEEMFLYER_MEMBER && flyer.IEEMFLYER_MEMBER.toLowerCase().includes(searchLower))
      );
    });
  }, [ieemFlyerData, searchTerm]);

  const flyerSummary = useMemo(() => getFlyerSummary(filteredFlyerData), [filteredFlyerData]);
  const flyerBlockers = useMemo(() => getFlyerBlockers(filteredFlyerData), [filteredFlyerData]);

  return (
    <>
      <VStack align="stretch" spacing={4} mb={6}>
        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
          <Card borderRadius="14px" border="1px solid" borderColor="gray.100">
            <CardBody py={3}>
              <Text fontSize="xs" color="gray.500">Total Flyer</Text>
              <Text fontSize="xl" fontWeight="700" color="gray.800" fontVariantNumeric="tabular-nums">
                {flyerSummary.total}
              </Text>
            </CardBody>
          </Card>
          <Card borderRadius="14px" border="1px solid" borderColor="green.100" bg="green.50">
            <CardBody py={3}>
              <Text fontSize="xs" color="green.700">Published</Text>
              <Text fontSize="xl" fontWeight="700" color="green.800" fontVariantNumeric="tabular-nums">
                {flyerSummary.published}
              </Text>
            </CardBody>
          </Card>
          <Card borderRadius="14px" border="1px solid" borderColor="orange.100" bg="orange.50">
            <CardBody py={3}>
              <Text fontSize="xs" color="orange.700">Draft</Text>
              <Text fontSize="xl" fontWeight="700" color="orange.800" fontVariantNumeric="tabular-nums">
                {flyerSummary.draft}
              </Text>
            </CardBody>
          </Card>
        </SimpleGrid>

        <ReadinessPanel blockers={flyerBlockers} />

        <HStack spacing={3} flexWrap="wrap">
          <Button
            leftIcon={<FiPlus />}
            onClick={handleIeemFlyerCreate}
            colorScheme="brand"
            size={{ base: 'sm', lg: 'md' }}
          >
            Tambah Data
          </Button>
          <Button
            leftIcon={<FiRefreshCw />}
            onClick={fetchIeemFlyer}
            isLoading={loadingIeemFlyer}
            variant="outline"
            colorScheme="brand"
            size={{ base: 'sm', lg: 'md' }}
          >
            Refresh
          </Button>
        </HStack>
      </VStack>

      <DataTableShell isLoading={loadingIeemFlyer}>
      <DataTable
        columns={[
          {
            name: 'ID',
            selector: (row) => row.IEEMFLYER_ID,
            sortable: true,
            width: '80px',
            omit: isMobileView,
          },
          {
            name: 'Nama',
            selector: (row) => row.IEEMFLYER_NAME || '-',
            sortable: true,
            cell: (row) => (
              <Text fontWeight="medium">
                {row.IEEMFLYER_NAME || '-'}
              </Text>
            ),
          },
          {
            name: 'Deskripsi',
            selector: (row) => row.IEEMFLYER_DESC || '-',
            sortable: true,
            cell: (row) => (
              <Text noOfLines={2} maxW="300px">
                {row.IEEMFLYER_DESC || '-'}
              </Text>
            ),
            omit: isMobileView,
          },
          {
            name: 'Member',
            selector: (row) => row.COMPANY_NAME || row.IEEMFLYER_MEMBER || '-',
            sortable: true,
            cell: (row) => (
              <Text>
                {row.COMPANY_NAME || row.IEEMFLYER_MEMBER || '-'}
              </Text>
            ),
            omit: isMobileView,
          },
          {
            name: 'File/URL',
            cell: (row) => {
              const fileUrl = getFileUrl(row);
              if (fileUrl) {
                return (
                  <HStack spacing={2}>
                    <Link
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="blue.500"
                      _hover={{ color: 'blue.600', textDecoration: 'underline' }}
                    >
                      <HStack spacing={1}>
                        <FiExternalLink />
                        <Text fontSize="sm">Buka</Text>
                      </HStack>
                    </Link>
                  </HStack>
                );
              }
              return <Text color="gray.400">-</Text>;
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
          },
          {
            name: 'Status',
            selector: (row) => getFlyerStatus(row),
            sortable: true,
            cell: (row) => <StatusChip status={getFlyerStatus(row)} />,
          },
          {
            name: 'Aksi',
            cell: (row) => (
              <HStack spacing={1} justify="center">
                <IconButton
                  icon={<FiEdit />}
                  size={{ base: 'xs', lg: 'sm' }}
                  colorScheme="blue"
                  variant="ghost"
                  onClick={() => handleIeemFlyerEdit(row)}
                  aria-label="Edit"
                />
                <IconButton
                  icon={<FiTrash2 />}
                  size={{ base: 'xs', lg: 'sm' }}
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => handleIeemFlyerDeleteClick(row)}
                  aria-label="Delete"
                />
              </HStack>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '120px',
          },
        ]}
        data={filteredFlyerData}
        progressPending={loadingIeemFlyer}
        progressComponent={<Text py={8}>Loading...</Text>}
        noDataComponent={
          <Text py={8} color="gray.500" textAlign="center">
            Tidak ada data IEEM Flyer
          </Text>
        }
        subHeader
        subHeaderComponent={
          <SubheaderToolbar
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Cari flyer (nama, deskripsi, ID, member)..."
            count={filteredFlyerData.length}
          />
        }
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
        paginationComponentOptions={paginationComponentOptions}
        highlightOnHover
        pointerOnHover
        customStyles={customStyles}
        responsive
        dense
        striped
      />
      </DataTableShell>

      {/* IEEM Flyer Form Modal */}
      <Modal isOpen={isIeemFlyerFormOpen} onClose={onIeemFlyerFormClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingIeemFlyer ? 'Edit IEEM Flyer' : 'Tambah IEEM Flyer Baru'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Nama</FormLabel>
                <Input
                  name="IEEMFLYER_NAME"
                  value={ieemFlyerFormData.IEEMFLYER_NAME}
                  onChange={handleIeemFlyerInputChange}
                  placeholder="Masukkan nama flyer"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Deskripsi</FormLabel>
                <Textarea
                  name="IEEMFLYER_DESC"
                  value={ieemFlyerFormData.IEEMFLYER_DESC}
                  onChange={handleIeemFlyerInputChange}
                  placeholder="Masukkan deskripsi flyer"
                  rows={4}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Member</FormLabel>
                <Input
                  name="IEEMFLYER_MEMBER"
                  value={ieemFlyerFormData.IEEMFLYER_MEMBER}
                  isReadOnly
                  bg="gray.50"
                />
              </FormControl>

              <FormControl>
                <FormLabel>File</FormLabel>
                <Input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png,.gif"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Format: PDF, JPG, PNG, GIF (Maks. 10MB)
                  {editingIeemFlyer && editingIeemFlyer.IEEMFLYER_FILE && (
                    <Text as="span" display="block" mt={1}>
                      File saat ini: {editingIeemFlyer.IEEMFLYER_FILE}
                    </Text>
                  )}
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>URL (Alternatif)</FormLabel>
                <Input
                  name="IEEMFLYER_URL"
                  value={ieemFlyerFormData.IEEMFLYER_URL}
                  onChange={handleIeemFlyerInputChange}
                  placeholder="Masukkan URL flyer (opsional)"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Jika URL diisi, file tidak wajib diupload
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onIeemFlyerFormClose}>
              Batal
            </Button>
            <Button 
              colorScheme="brand" 
              onClick={handleIeemFlyerSubmit}
              isLoading={uploadingFile}
            >
              {editingIeemFlyer ? 'Update' : 'Simpan'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* IEEM Flyer Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isIeemFlyerDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onIeemFlyerDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Hapus IEEM Flyer
            </AlertDialogHeader>

            <AlertDialogBody>
              Apakah Anda yakin ingin menghapus flyer <strong>{deleteIeemFlyer?.IEEMFLYER_NAME}</strong>?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onIeemFlyerDeleteClose}>
                Batal
              </Button>
              <Button colorScheme="red" onClick={handleIeemFlyerDeleteConfirm} ml={3}>
                Hapus
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default IeemFlyerTab;

